import { useEffect, useState } from "react";
import { CheckCircle2, Truck, ShieldCheck } from "lucide-react";
import { getJson } from "../lib/http";
import staticContent from "../lib/staticContent.json";

// Default shown immediately while the live value is fetched — baked at
// build time (sync-static-catalog.js) so it matches the admin-configured
// value and the banner text doesn't shift when the fetch resolves.
const FALLBACK_FREE_SHIPPING =
  Number(staticContent.free_shipping_threshold) > 0
    ? Number(staticContent.free_shipping_threshold)
    : 250;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DisclaimerBanner() {
  // Fetch the admin-configured threshold once on mount; the API is public
  // (returns only the two numbers already visible in checkout responses)
  // so no auth is needed. Cached in component state — the banner is global
  // so this fetch fires exactly once per page load.
  const [threshold, setThreshold] = useState(FALLBACK_FREE_SHIPPING);

  useEffect(() => {
    let cancelled = false;
    getJson(`${API}/shipping-info`)
      .then((data) => {
        if (cancelled) return;
        const v = Number(data?.free_shipping_threshold);
        if (Number.isFinite(v) && v > 0) setThreshold(v);
      })
      .catch(() => {
        // Network errors leave the banner on the fallback value — better
        // than rendering "$undefined" or hiding the promo entirely.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Whole dollars look cleaner in the banner ($250 not $250.00), but if
  // the admin set a precise value like $249.95 we keep both decimals.
  const formatted = Number.isInteger(threshold)
    ? `$${threshold}`
    : `$${threshold.toFixed(2)}`;

  const promos = [
    {
      icon: ShieldCheck,
      // Short label used on mobile (< sm breakpoint) to keep the
      // disclaimer bar to a single line under the notch; longer copy
      // shows from sm+ where there's horizontal room.
      text: "Research use only · not for human consumption",
      shortText: "Research use only",
    },
    { icon: Truck, text: `Free shipping over ${formatted}` },
    { icon: CheckCircle2, text: "Xpresspost 1–2 business days" },
  ];

  return (
    <div
      data-testid="disclaimer-banner"
      className="w-full bg-[#20a6da] text-[#0a0a0a]"
      style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-2 flex items-center justify-center gap-6 sm:gap-10 flex-wrap text-center">
        {promos.map((p, i) => (
          <span
            key={i}
            className={`${
              i > 0 ? "hidden sm:inline-flex" : "inline-flex"
            } items-center gap-2 text-[11px] sm:text-xs font-semibold uppercase tracking-[0.14em]`}
          >
            <p.icon size={13} strokeWidth={2.25} />
            {p.shortText ? (
              <>
                <span className="sm:hidden">{p.shortText}</span>
                <span className="hidden sm:inline">{p.text}</span>
              </>
            ) : (
              p.text
            )}
          </span>
        ))}
      </div>
    </div>
  );
}
